import { INDICATOR_DEFINITIONS } from "../indicators/indicator-definitions.js";
import { createClientId } from "../core/client-id.js";

const DATABASE_NAME = "flexcharting-local-alerts";
const DATABASE_VERSION = 1;
const ALERTS_STORE = "alerts";
const TRIGGERS_STORE = "triggers";
const RESOURCES_STORE = "resources";
const SUPPORTED_INDICATORS = new Set(["sma", "ema", "rsi", "macd", "bollinger"]);

export class LocalAlertRepository {
  constructor({
    storageScope = "anonymous",
    maximumActiveAlerts = Number.POSITIVE_INFINITY,
    resolveSymbol = () => "",
    onMutation = () => {}
  } = {}) {
    this.resolveSymbol = resolveSymbol;
    this.onMutation = onMutation;
    this.maximumActiveAlerts = Number.isFinite(maximumActiveAlerts)
      ? Math.max(0, Number(maximumActiveAlerts))
      : Number.POSITIVE_INFINITY;
    this.databasePromise = openDatabase(`${DATABASE_NAME}-${storageScope}`);
    this.capabilityCatalog = createLocalCapabilityCatalog();
  }

  async listAll() {
    const alerts = await readAll(await this.databasePromise, ALERTS_STORE);
    const now = Date.now();
    const expired = alerts.filter((alert) =>
      alert.status !== "Expired"
      && alert.expiresAtUtc
      && Date.parse(alert.expiresAtUtc) <= now
    );
    if (expired.length) {
      const database = await this.databasePromise;
      await runTransaction(database, ALERTS_STORE, "readwrite", (store) => {
        for (const alert of expired) {
          store.put({
            ...alert,
            status: "Expired",
            version: Number(alert.version || 0) + 1,
            updatedAtUtc: new Date().toISOString()
          });
        }
      });
      return readAll(database, ALERTS_STORE);
    }
    return alerts.sort(newestFirst);
  }

  async history({ isRead = null } = {}) {
    const triggers = await readAll(await this.databasePromise, TRIGGERS_STORE);
    return triggers
      .filter((trigger) => isRead === null || trigger.isRead === isRead)
      .sort((left, right) => Date.parse(right.triggeredAtUtc) - Date.parse(left.triggeredAtUtc));
  }

  async create(payload) {
    const now = new Date().toISOString();
    const alert = alertFromPayload(payload, {
      id: createClientId(),
      version: 1,
      symbol: this.resolveSymbol(payload.symbolId),
      status: "Active",
      activatedAtUtc: now,
      calculationReadiness: isIndicatorType(payload.type) ? "Ready" : "NotApplicable",
      createdAtUtc: now,
      updatedAtUtc: now
    });
    const database = await this.databasePromise;
    await runTransaction(database, ALERTS_STORE, "readwrite", async (store) => {
      ensureActiveAlertCapacity(await requestResult(store.getAll()), this.maximumActiveAlerts);
      store.add(alert);
    });
    this.onMutation("alerts");
    return alert;
  }

  async update(alert, payload) {
    const database = await this.databasePromise;
    const result = await runTransaction(database, ALERTS_STORE, "readwrite", async (store) => {
      const current = await requestResult(store.get(alert.id));
      requireVersion(current, alert.version);
      const calculationChanged = calculationFingerprint(current)
        !== calculationFingerprint(alertFromPayload(payload, current));
      const next = alertFromPayload(payload, {
        ...current,
        version: Number(current.version) + 1,
        status: current.status === "Expired" ? "Expired" : "Active",
        calculationReadiness: isIndicatorType(payload.type) ? "Ready" : "NotApplicable",
        updatedAtUtc: new Date().toISOString(),
        ...(calculationChanged ? {
          lastEvaluatedAtUtc: null,
          currentIndicatorValue: null,
          currentIndicatorBValue: null,
          currentComparisonPrice: null
        } : {})
      });
      if (current.status !== "Active" && next.status === "Active") {
        ensureActiveAlertCapacity(
          await requestResult(store.getAll()),
          this.maximumActiveAlerts,
          current.id
        );
      }
      store.put(next);
      return next;
    });
    this.onMutation("alerts");
    return result;
  }

  async action(alert, action) {
    if (action === "duplicate") {
      return this.create({
        ...payloadFromAlert(alert),
        name: `${alert.name} copy`
      });
    }
    const statuses = {
      pause: "Paused",
      resume: "Active",
      rearm: "Active"
    };
    if (!statuses[action]) throw new Error("This alert action is not supported.");
    const database = await this.databasePromise;
    const result = await runTransaction(database, ALERTS_STORE, "readwrite", async (store) => {
      const current = await requestResult(store.get(alert.id));
      requireVersion(current, alert.version);
      if (current.status !== "Active" && statuses[action] === "Active") {
        ensureActiveAlertCapacity(
          await requestResult(store.getAll()),
          this.maximumActiveAlerts,
          current.id
        );
      }
      const next = {
        ...current,
        status: statuses[action],
        version: Number(current.version) + 1,
        updatedAtUtc: new Date().toISOString(),
        ...(action === "rearm" || action === "resume" ? {
          activatedAtUtc: new Date().toISOString(),
          calculationReadiness: isIndicatorType(current.type) ? "Ready" : current.calculationReadiness
        } : {})
      };
      store.put(next);
      return next;
    });
    this.onMutation("alerts");
    return result;
  }

  async remove(alert) {
    const database = await this.databasePromise;
    const result = await runTransaction(database, ALERTS_STORE, "readwrite", async (store) => {
      const current = await requestResult(store.get(alert.id));
      requireVersion(current, alert.version);
      store.delete(alert.id);
      return null;
    });
    this.onMutation("alerts");
    return result;
  }

  async bulk(action, ids) {
    for (const id of ids) {
      const alerts = await this.listAll();
      const alert = alerts.find((item) => item.id === id);
      if (!alert) continue;
      if (action === "delete") await this.remove(alert);
      else await this.action(alert, action);
    }
    return { count: ids.length };
  }

  async unreadCount() {
    const unread = await this.history({ isRead: false });
    return { count: unread.length };
  }

  async markRead(triggerId) {
    const database = await this.databasePromise;
    const result = await runTransaction(database, TRIGGERS_STORE, "readwrite", async (store) => {
      const current = await requestResult(store.get(triggerId));
      if (!current) return null;
      const next = { ...current, isRead: true };
      store.put(next);
      return next;
    });
    this.onMutation("triggers");
    return result;
  }

  async markAllRead() {
    const database = await this.databasePromise;
    await runTransaction(database, TRIGGERS_STORE, "readwrite", async (store) => {
      const triggers = await requestResult(store.getAll());
      for (const trigger of triggers) store.put({ ...trigger, isRead: true });
    });
    this.onMutation("triggers");
    return { count: 0 };
  }

  async capabilities() {
    return this.capabilityCatalog;
  }

  async previewIndicator(payload) {
    const capability = this.capabilityCatalog.indicators.find(
      (indicator) => indicator.id === payload.indicator?.indicatorId
    );
    const output = capability?.outputs.find(
      (candidate) => candidate.id === payload.indicator?.outputSeries
    );
    const validationErrors = {};
    if (!capability || !output) validationErrors.indicator = ["Choose a supported indicator output."];
    if (payload.type === "PriceIndicator" && !output?.supportsPriceComparison) {
      validationErrors.condition = ["This indicator output cannot be compared with price."];
    }
    return {
      conditionDescription: localConditionDescription(payload, capability, output),
      readiness: Object.keys(validationErrors).length ? "CalculationError" : "Ready",
      currentIndicatorValue: null,
      validationErrors
    };
  }

  async previewIndicatorPair(payload) {
    const capabilityA = this.capabilityCatalog.indicators.find(
      (indicator) => indicator.id === payload.indicatorA?.indicatorId
    );
    const capabilityB = this.capabilityCatalog.indicators.find(
      (indicator) => indicator.id === payload.indicatorB?.indicatorId
    );
    const outputA = capabilityA?.outputs.find(
      (output) => output.id === payload.indicatorA?.outputSeries
    );
    const outputB = capabilityB?.outputs.find(
      (output) => output.id === payload.indicatorB?.outputSeries
    );
    const compatible = outputA && outputB
      && outputA.comparisonUnitKey === outputB.comparisonUnitKey;
    return {
      conditionDescription: `${outputA?.displayName || "Indicator A"} ${
        operatorWords(payload.operator)
      } ${outputB?.displayName || "Indicator B"}`,
      readiness: compatible ? "Ready" : "CalculationError",
      currentIndicatorAValue: null,
      currentIndicatorBValue: null,
      currentDifference: null,
      sharedWatermark: null,
      validationErrors: compatible
        ? {}
        : { condition: ["Choose indicator outputs with compatible units."] }
    };
  }

  upsertIndicator(chartId, indicatorId) {
    return this.upsertResource(`indicator:${chartId}:${indicatorId}`);
  }

  deleteIndicator(chartId, indicatorId) {
    return this.deleteResource(`indicator:${chartId}:${indicatorId}`);
  }

  upsertDrawing(chartId, drawingId) {
    return this.upsertResource(`drawing:${chartId}:${drawingId}`);
  }

  deleteDrawing(chartId, drawingId) {
    return this.deleteResource(`drawing:${chartId}:${drawingId}`);
  }

  async upsertResource(id) {
    const database = await this.databasePromise;
    return runTransaction(database, RESOURCES_STORE, "readwrite", async (store) => {
      const current = await requestResult(store.get(id));
      const next = { id, version: Number(current?.version || 0) + 1 };
      store.put(next);
      return next;
    });
  }

  async deleteResource(id) {
    const database = await this.databasePromise;
    await runTransaction(database, RESOURCES_STORE, "readwrite", (store) => store.delete(id));
  }

  async recordTrigger(result) {
    const database = await this.databasePromise;
    const recorded = await runTransaction(
      database,
      [ALERTS_STORE, TRIGGERS_STORE],
      "readwrite",
      async (stores) => {
        const alertStore = stores[ALERTS_STORE];
        const triggerStore = stores[TRIGGERS_STORE];
        const alert = await requestResult(alertStore.get(result.alertId));
        if (!alert || !["Active", "Triggered"].includes(alert.status)) {
          return { created: false, trigger: null };
        }
        const existing = await requestResult(
          triggerStore.index("occurrenceKey").get(result.occurrenceKey)
        );
        if (existing) return { created: false, trigger: existing };

        const triggeredAtUtc = result.observedAtUtc;
        const trigger = {
          id: createClientId(),
          alertId: alert.id,
          eventId: result.eventId,
          occurrenceKey: result.occurrenceKey,
          symbolId: alert.symbolId,
          symbol: alert.symbol,
          alertName: alert.name,
          conditionSnapshot: conditionForAlert(alert),
          renderedMessage: alert.customMessage || triggerMessage(alert, result),
          observedPrice: result.price,
          indicatorValue: result.indicatorValue,
          indicatorBValue: result.indicatorBValue,
          thresholdPrice: alert.thresholdPrice,
          timeframe: alert.timeframe,
          triggerTiming: alert.triggerTiming,
          barId: result.barId,
          triggeredAtUtc,
          marketTimestampUtc: triggeredAtUtc,
          isRead: false,
          deliveryStatus: "Delivered"
        };
        triggerStore.add(trigger);
        alertStore.put({
          ...alert,
          status: alert.frequency === "Once" ? "Triggered" : "Active",
          version: Number(alert.version) + 1,
          updatedAtUtc: triggeredAtUtc,
          lastEvaluatedAtUtc: triggeredAtUtc,
          lastTriggeredAtUtc: triggeredAtUtc,
          triggerCount: Number(alert.triggerCount || 0) + 1,
          currentIndicatorValue: result.indicatorValue,
          currentIndicatorBValue: result.indicatorBValue,
          currentComparisonPrice: result.price
        });
        return { created: true, trigger };
      }
    );
    if (recorded.created) this.onMutation("triggered", recorded.trigger);
    return recorded;
  }

  async expire(alertId) {
    const database = await this.databasePromise;
    await runTransaction(database, ALERTS_STORE, "readwrite", async (store) => {
      const alert = await requestResult(store.get(alertId));
      if (!alert || alert.status === "Expired") return;
      store.put({
        ...alert,
        status: "Expired",
        version: Number(alert.version) + 1,
        updatedAtUtc: new Date().toISOString()
      });
    });
    this.onMutation("alerts");
  }
}

export function createLocalCapabilityCatalog() {
  return {
    schemaVersion: 1,
    indicators: INDICATOR_DEFINITIONS
      .filter((definition) => SUPPORTED_INDICATORS.has(definition.id))
      .map((definition) => {
        const priceUnit = ["sma", "ema", "bollinger"].includes(definition.id);
        return {
          id: definition.id,
          displayName: definition.name,
          description: definition.description,
          algorithmVersion: "chart-js-1",
          isAvailable: true,
          isDeprecated: false,
          supportsImmediate: true,
          supportsCompletedBar: true,
          supportedInputSeries: ["open", "high", "low", "close", "hl2", "hlc3", "ohlc4"],
          parameters: definition.inputs.map(capabilityParameter),
          outputs: definition.outputs
            .filter((output) => !["fill", "level"].includes(output.kind))
            .map((output) => ({
              id: output.key,
              displayName: output.label,
              unit: priceUnit ? "Price" : "Number",
              comparisonUnitKey: priceUnit ? "price" : "number",
              comparisonDomain: priceUnit ? "Price" : "Scalar",
              allowedComparisonDomains: priceUnit
                ? ["Price"]
                : ["Scalar"],
              supportsPriceComparison: priceUnit,
              supportsFixedValueComparison: true,
              displayPrecision: priceUnit ? 4 : 6
            }))
        };
      })
  };
}

function capabilityParameter(input) {
  const type = {
    integer: "Integer",
    decimal: "Decimal",
    level: "Decimal",
    boolean: "Boolean",
    select: "String",
    source: "String"
  }[input.type] || "String";
  return {
    id: input.key,
    displayName: input.label,
    type,
    defaultValue: String(input.defaultValue),
    minimum: input.minimum ?? null,
    maximum: input.maximum ?? null,
    step: input.step ?? null,
    options: input.options
      ? Object.fromEntries(input.options.map((option) => [option.value, option.label]))
      : null
  };
}

function alertFromPayload(payload, base) {
  const indicator = payload.indicator || null;
  const indicatorB = payload.indicatorB || null;
  return {
    ...base,
    name: payload.name,
    customMessage: payload.customMessage || null,
    symbolId: payload.symbolId || base.symbolId,
    symbol: base.symbol || payload.symbol || payload.symbolId,
    type: payload.type,
    operator: payload.operator,
    thresholdPrice: Number(payload.thresholdPrice),
    timeframe: payload.timeframe || null,
    triggerTiming: payload.triggerTiming,
    frequency: payload.frequency,
    session: payload.session,
    expiresAtUtc: payload.expiresAtUtc || null,
    notificationChannels: "InApp",
    sourceChartId: payload.sourceChartId || null,
    sourceDrawingId: payload.sourceDrawingId || null,
    indicatorId: indicator?.indicatorId || null,
    indicatorAlgorithmVersion: indicator?.algorithmVersion || null,
    indicatorParametersJson: indicator ? JSON.stringify(indicator.parameters || {}) : null,
    indicatorInputSeries: indicator?.inputSeries || null,
    indicatorOutputSeries: indicator?.outputSeries || null,
    indicatorOutputDisplayName: indicator
      ? outputName(indicator.indicatorId, indicator.outputSeries)
      : null,
    indicatorOutputUnit: indicator ? outputUnit(indicator.indicatorId) : null,
    adjustmentMode: indicator?.adjustmentMode || "Unadjusted",
    sourceIndicatorId: indicator?.sourceIndicatorId || null,
    sourceIndicatorVersion: indicator?.sourceIndicatorVersion || null,
    indicatorBId: indicatorB?.indicatorId || null,
    indicatorBAlgorithmVersion: indicatorB?.algorithmVersion || null,
    indicatorBParametersJson: indicatorB ? JSON.stringify(indicatorB.parameters || {}) : null,
    indicatorBInputSeries: indicatorB?.inputSeries || null,
    indicatorBOutputSeries: indicatorB?.outputSeries || null,
    indicatorBOutputDisplayName: indicatorB
      ? outputName(indicatorB.indicatorId, indicatorB.outputSeries)
      : null,
    sourceIndicatorBId: indicatorB?.sourceIndicatorId || null,
    sourceIndicatorBVersion: indicatorB?.sourceIndicatorVersion || null,
    pricePrecision: base.pricePrecision ?? 4,
    tickSize: base.tickSize ?? 0.0001
  };
}

function payloadFromAlert(alert) {
  return {
    name: alert.name,
    customMessage: alert.customMessage,
    symbolId: alert.symbolId,
    symbol: alert.symbol,
    type: alert.type,
    operator: alert.operator,
    thresholdPrice: alert.thresholdPrice,
    timeframe: alert.timeframe,
    triggerTiming: alert.triggerTiming,
    frequency: alert.frequency,
    session: alert.session,
    expiresAtUtc: alert.expiresAtUtc,
    notificationChannels: "InApp",
    sourceChartId: alert.sourceChartId,
    sourceDrawingId: alert.sourceDrawingId,
    indicator: descriptorFromAlert(alert, ""),
    indicatorB: descriptorFromAlert(alert, "B")
  };
}

function descriptorFromAlert(alert, suffix) {
  const id = alert[`indicator${suffix}Id`];
  if (!id) return null;
  return {
    schemaVersion: 1,
    indicatorId: id,
    algorithmVersion: alert[`indicator${suffix}AlgorithmVersion`],
    parameters: parseJson(alert[`indicator${suffix}ParametersJson`]),
    inputSeries: alert[`indicator${suffix}InputSeries`],
    outputSeries: alert[`indicator${suffix}OutputSeries`],
    adjustmentMode: alert.adjustmentMode || "Unadjusted",
    sourceIndicatorId: alert[`sourceIndicator${suffix}Id`],
    sourceIndicatorVersion: alert[`sourceIndicator${suffix}Version`]
  };
}

function calculationFingerprint(alert) {
  return JSON.stringify([
    alert.symbolId,
    alert.type,
    alert.operator,
    alert.thresholdPrice,
    alert.timeframe,
    alert.triggerTiming,
    alert.session,
    alert.indicatorId,
    alert.indicatorAlgorithmVersion,
    alert.indicatorParametersJson,
    alert.indicatorInputSeries,
    alert.indicatorOutputSeries,
    alert.indicatorBId,
    alert.indicatorBAlgorithmVersion,
    alert.indicatorBParametersJson,
    alert.indicatorBInputSeries,
    alert.indicatorBOutputSeries,
    alert.adjustmentMode
  ]);
}

function conditionForAlert(alert) {
  if (!alert.indicatorId) {
    return `Price ${operatorWords(alert.operator)} ${alert.thresholdPrice}`;
  }
  const left = indicatorLabel(
    alert.indicatorId,
    alert.indicatorParametersJson,
    alert.indicatorOutputDisplayName
  );
  if (alert.type === "PriceIndicator") return `Price ${operatorWords(alert.operator)} ${left}`;
  if (alert.type === "IndicatorPair") {
    const right = indicatorLabel(
      alert.indicatorBId,
      alert.indicatorBParametersJson,
      alert.indicatorBOutputDisplayName
    );
    return `${left} ${operatorWords(alert.operator)} ${right}`;
  }
  return `${left} ${operatorWords(alert.operator)} ${alert.thresholdPrice}`;
}

function triggerMessage(alert, result) {
  const condition = conditionForAlert(alert);
  const price = Number.isFinite(result.price) ? ` at ${result.price}` : "";
  return `${alert.symbol} ${condition}${price}`;
}

function indicatorLabel(id, parametersJson, outputNameValue) {
  const definition = INDICATOR_DEFINITIONS.find((item) => item.id === id);
  const parameters = parseJson(parametersJson);
  const summary = (definition?.summaryInputs || [])
    .filter((key) => key !== "source")
    .map((key) => parameters[key])
    .filter((value) => value !== undefined)
    .join(", ");
  return `${outputNameValue || definition?.abbreviation || id?.toUpperCase()}${
    summary ? ` (${summary})` : ""
  }`;
}

function localConditionDescription(payload, capability, output) {
  const indicator = output?.displayName || capability?.displayName || "Indicator";
  if (payload.type === "PriceIndicator") {
    return `Price ${operatorWords(payload.operator)} ${indicator}`;
  }
  return `${indicator} ${operatorWords(payload.operator)} ${payload.thresholdPrice}`;
}

function outputName(indicatorId, outputId) {
  const definition = INDICATOR_DEFINITIONS.find((item) => item.id === indicatorId);
  return definition?.outputs.find((output) => output.key === outputId)?.label || outputId;
}

function outputUnit(indicatorId) {
  return ["sma", "ema", "bollinger"].includes(indicatorId) ? "Price" : "Number";
}

function operatorWords(operator) {
  return {
    CrossesAbove: "crosses above",
    CrossesBelow: "crosses below",
    CrossesEither: "crosses",
    GreaterThan: "is greater than",
    LessThan: "is less than"
  }[operator] || operator;
}

function requireVersion(current, expectedVersion) {
  if (!current) throw new Error("The alert no longer exists.");
  if (Number(current.version) !== Number(expectedVersion)) {
    const error = new Error("This alert changed in another tab. Reload and try again.");
    error.status = 409;
    throw error;
  }
}

function isIndicatorType(type) {
  return ["IndicatorValue", "PriceIndicator", "IndicatorPair"].includes(type);
}

function parseJson(value) {
  if (!value) return {};
  if (typeof value === "object") return { ...value };
  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
}

function newestFirst(left, right) {
  return Date.parse(right.updatedAtUtc || 0) - Date.parse(left.updatedAtUtc || 0);
}

function openDatabase(databaseName) {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(databaseName, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(ALERTS_STORE)) {
        database.createObjectStore(ALERTS_STORE, { keyPath: "id" });
      }
      if (!database.objectStoreNames.contains(TRIGGERS_STORE)) {
        const triggers = database.createObjectStore(TRIGGERS_STORE, { keyPath: "id" });
        triggers.createIndex("occurrenceKey", "occurrenceKey", { unique: true });
      }
      if (!database.objectStoreNames.contains(RESOURCES_STORE)) {
        database.createObjectStore(RESOURCES_STORE, { keyPath: "id" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error("Local alert storage is unavailable."));
  });
}

function readAll(database, storeName) {
  return runTransaction(database, storeName, "readonly", (store) =>
    requestResult(store.getAll()));
}

function runTransaction(database, storeNames, mode, operation) {
  return new Promise((resolve, reject) => {
    const names = Array.isArray(storeNames) ? storeNames : [storeNames];
    const transaction = database.transaction(names, mode);
    const stores = Object.fromEntries(
      names.map((name) => [name, transaction.objectStore(name)])
    );
    let result;
    Promise.resolve(operation(
      Array.isArray(storeNames) ? stores : stores[storeNames]
    )).then((value) => {
      result = value;
    }).catch((error) => {
      try {
        transaction.abort();
      } catch {
        // The original operation error remains the actionable failure.
      }
      reject(error);
    });
    transaction.oncomplete = () => resolve(result);
    transaction.onerror = () => reject(
      transaction.error || new Error("The local alert transaction failed.")
    );
    transaction.onabort = () => reject(
      transaction.error || new Error("The local alert transaction was cancelled.")
    );
  });
}

function requestResult(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function ensureActiveAlertCapacity(alerts, maximum, excludedId = null) {
  if (!Number.isFinite(maximum)) return;
  const active = (alerts || []).filter((alert) =>
    alert?.id !== excludedId && alert?.status === "Active").length;
  if (active < maximum) return;
  const error = new Error(maximum === 0
    ? "This product does not include alerts."
    : "You have reached the active alert limit for this product.");
  error.name = "LocalAlertQuotaError";
  error.code = maximum === 0 ? "capability-disabled" : "quota-exceeded";
  throw error;
}
